/**
 * The single source of truth for role-based and capability-based UI gating in the SPA.
 *
 * Gating idioms:
 *   - Navigation entries: hide entirely when canAccessFeature returns false
 *     (sidebar leaves/groups not rendered when the user lacks the required tier).
 *   - In-context actions: disable the control with a tooltip explaining the
 *     required role, e.g. "Requires Operator — you are Viewer".
 *   - Client-side gating is courtesy UX only; FastAPI enforces at every
 *     write endpoint and returns role-aware 403 copy.
 *
 * isAdmin:    role === 'admin'
 * isOperator: role === 'write' or 'admin' (write tier or above)
 * isViewer:   any non-null role (authenticated with at least read access)
 *
 * awaitingGrant: true when the WS handshake was refused with authz_denied,
 * meaning the user is authenticated but has not yet been assigned a role.
 * Surfaces a contextual message rather than silent breakage.
 *
 * canAccessFeature: gates on the server-computed capabilities map from /auth/me,
 * so sidebar visibility is a projection of the same access the route gate uses —
 * never a second hand-maintained list. The 'core' area is always visible (D-9).
 */

import { useCallback, useMemo } from 'react'
import { useCurrentUserContext } from '@/contexts/currentUser'
import type { MeResponse, GrantTier, UserRole } from '@/types/auth'

// Rank table mirrors the backend _TIER_RANK so comparisons are consistent.
// 'none' is the rank-0 deny; a missing capability key also resolves to 'none'.
const CAPABILITY_RANK: Record<GrantTier, number> = { none: 0, read: 1, write: 2, admin: 3 }

export interface CurrentUser {
  user: MeResponse | null
  role: UserRole | null
  isAdmin: boolean
  isOperator: boolean
  isViewer: boolean
  isLoading: boolean
  awaitingGrant: boolean
  /** Per-feature effective tier from /auth/me — authoritative for visibility. */
  capabilities: Record<string, GrantTier>
  /**
   * True when the principal meets minRank for featureArea. The cross-cutting
   * 'core' area is always visible (the always-on support surface — D-9). Drives
   * sidebar leaf/group rendering — a projection of server-computed access, not a
   * second list. The empty-map default keeps every non-core leaf hidden until
   * /auth/me resolves, so a forbidden leaf is never briefly flashed.
   */
  canAccessFeature: (featureArea: string, minRank?: GrantTier) => boolean
}

export function useCurrentUser(): CurrentUser {
  const { user, isLoading, awaitingGrant } = useCurrentUserContext()
  const role = user?.role ?? null
  // Memoised so the object reference is stable when capabilities haven't changed,
  // preventing canAccessFeature from being recreated on every render.
  const capabilities = useMemo(
    () => user?.capabilities ?? {},
    [user?.capabilities],
  )

  const canAccessFeature = useCallback(
    (featureArea: string, minRank: GrantTier = 'read'): boolean => {
      if (featureArea === 'core') return true                   // always-visible (D-9)
      const have = capabilities[featureArea] ?? 'none'
      return CAPABILITY_RANK[have] >= CAPABILITY_RANK[minRank]
    },
    [capabilities],
  )

  return {
    user,
    role,
    isAdmin: role === 'admin',
    isOperator: role === 'write' || role === 'admin',
    isViewer: role !== null,
    isLoading,
    awaitingGrant,
    capabilities,
    canAccessFeature,
  }
}
